import type { WPUser } from "../../../lib/api";
import { useState } from "react";
import { mockUser } from "../../../mocks/accountData";

interface ProfileTabProps { user?: WPUser | null; }
export default function ProfileTab({ user }: ProfileTabProps) {
  const [name, setName] = useState(user ? `${user.first_name} ${user.last_name}`.trim() : mockUser.name);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [email, setEmail] = useState((user?.email ?? mockUser.email));
  const [phone, setPhone] = useState(mockUser.phone);
  const [postcode, setPostcode] = useState(mockUser.postcode);
  const [saved, setSaved] = useState(false);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifSms, setNotifSms] = useState(true);
  const [notifMarketing, setNotifMarketing] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-black text-cl-dark">Profile &amp; Settings</h2>
        <p className="text-gray-500 text-sm mt-0.5">Update your personal details and preferences</p>
      </div>

      {/* Profile form */}
      <div className="bg-white rounded-2xl thick-border cartoon-shadow p-6 mb-6">
        <h3 className="font-black text-cl-dark text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
          <i className="ri-user-line text-cl-green"></i>
          Personal Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark font-semibold"
            />
          </div>
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark font-semibold"
            />
          </div>
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark font-semibold"
            />
          </div>
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Home Postcode</label>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cl-green text-cl-dark font-semibold"
            />
          </div>
        </div>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 font-black text-sm px-6 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
            saved
              ? "bg-cl-green text-white"
              : "bg-cl-dark text-white cartoon-btn"
          }`}
        >
          <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          {saved ? "Saved!" : "Save Changes"}
        </button>
      </div>

      {/* Notification preferences */}
      <div className="bg-white rounded-2xl thick-border cartoon-shadow p-6 mb-6">
        <h3 className="font-black text-cl-dark text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
          <i className="ri-notification-line text-cl-green"></i>
          Notification Preferences
        </h3>
        <div className="flex flex-col gap-4">
          {[
            { label: "Booking confirmations & reminders", sub: "Get notified when a booking is confirmed or approaching", state: notifEmail, setter: setNotifEmail },
            { label: "Driver tracking updates", sub: "Live SMS updates when your driver is en route", state: notifSms, setter: setNotifSms },
            { label: "Offers &amp; promotions", sub: "Seasonal discounts and early-access deals from Cleario", state: notifMarketing, setter: setNotifMarketing },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-between gap-4 py-3 border-b border-transparent last:border-0">
              <div>
                <p className="font-bold text-cl-dark text-sm" dangerouslySetInnerHTML={{ __html: item.label }}></p>
                <p className="text-gray-400 text-xs mt-0.5">{item.sub}</p>
              </div>
              <button
                onClick={() => item.setter(!item.state)}
                className={`relative w-11 h-6 rounded-full border-2 border-cl-dark transition-colors cursor-pointer flex-shrink-0 ${
                  item.state ? "bg-cl-lime" : "bg-gray-200"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-cl-dark rounded-full transition-transform ${
                    item.state ? "translate-x-[22px]" : "translate-x-0.5"
                  }`}
                ></span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-2xl border-2 border-red-200 p-6">
        <h3 className="font-black text-red-500 text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          Account Actions
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="flex items-center gap-2 text-sm font-bold border-2 border-transparent text-gray-500 hover:border-cl-dark hover:text-cl-dark px-4 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-lock-password-line"></i>
            Change Password
          </button>
          <button className="flex items-center gap-2 text-sm font-bold border-2 border-red-200 text-red-500 hover:bg-red-50 px-4 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-delete-bin-2-line"></i>
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
}
